import {
  Box,
  Button,
  Center,
  Menu,
  MenuButton,
  Spacer,
  Spinner,
  Switch,
  Text,
  useColorMode
} from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty, isUndefined } from 'lodash';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import AdminProtected from '../../components/admin/AdminProtected';
import CommonDialog from '../../components/alerts/CommonDialog';
import EventResponse from '../../components/api/model/EventResponse';
import ConfirmPhotoCard from '../../components/api/mutations/admin/ConfirmPhotoCard';
import RejectPhotoCard from '../../components/api/mutations/admin/RejectPhotoCard';
import useAdminPhotoCardEventsQuery from '../../components/api/queries/useAdminPhotoCardEventsQuery';
import { useArtistQueryAll } from '../../components/api/queries/useArtistQuery';
import MyPhotoCardBottomSheet from '../../components/bottomSheet/MyPhotoCardBottomSheet';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import SecondaryLineButton from '../../components/buttons/SecondaryLineButton';
import PhotoCardFilterBottomDrawer from '../../components/drawer/PhotoCardFilterBottomDrawer';
import EmptyCase from '../../components/errors/EmptyCase';
import LikedMembersList from '../../components/lists/LikedMembersList';
import ArtistTextSelector from '../../components/selectors/ArtistTextSelector';
import EmptyPhotoCardEventSkeletonV2 from '../../components/skeletons/EmptyPhotoCardEventSkeletonV2';
import PhotoCardsByEventEditor from '../../components/virtualized/PhotoCardsByEvent/PhotoCardsByEventEditor';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow.left.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron.down.svg';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import BoxTabs from '../tabs/BoxTabs';

const PageWrapper = styled.div`
  padding-top: calc(env(safe-area-inset-top));
  background-attachment: scroll;
  height: 100vh;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const LikedMembersListWrapper = styled.div`
  padding-top: 6px;
  padding-bottom: 16px;
  margin-bottom: 96px;
  padding-left: 24px;
  padding-right: 24px;
`;

const FilterButtonText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 17px
`;

export default function ConfirmReportedPhotoCardsPage() {
  const queryClient = useQueryClient();
  const { colorMode } = useColorMode();
  const handleHistoryBack = useHandleHistoryBack();

  // #region states & variables
  const [isOpenExitDialog, setIsOpenExitDialog] = useState(false);
  const [isMyPhotoCardsBottomSheetOpen, setIsMyPhotoCardsBottomSheetOpen] = useState(false);
  const [isShowFilterBottomDrawer, setIsShowFilterBottomDrawer] = useState(false);

  const [boxTabIndex, setBoxTabIndex] = useState(0);
  const [currentArtist, setCurrentArtist] = useState();
  const [currentMember, setCurrentMember] = useState();

  // Initialize state for paginated data and updated data
  const [paginatedEvents, setPaginatedEvents] = useState();
  const [checkecdPhotoCardIds, setCheckecdPhotoCardIds] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [selectedPhotoCardCategories, setSelectedPhotoCardCategories] = useState([]);
  const [isPendingMode, setIsPendingMode] = useState(false);

  // 자동 pagination을 위한 상태 추가
  const [isAutoFetching, setIsAutoFetching] = useState(false);
  const [hasAttemptedFullLoad, setHasAttemptedFullLoad] = useState(false);

  // #endregion

  // #region API
  const artists = useArtistQueryAll();
  const { data, isFetching, fetchNextPage, hasNextPage } = useAdminPhotoCardEventsQuery({
    artistId: currentArtist?.artistId,
    memberId: currentMember?.memberId,
    eventIds: selectedEventIds,
    photoCardCategories: selectedPhotoCardCategories,
    adminMode: isPendingMode ? 'pending' : 'all',
  });
  // #endregion

  // 실제 photoCard 데이터가 있는지 확인하는 함수
  const hasValidPhotoCards = (events) => {
    if (!events || isEmpty(events)) return false;
    return events.some((event) => !isEmpty(event.photoCards));
  };

  // #region Function to handle updated data set.
  const handleConfirmedPhotoCard = (photoCard) => {
    setCheckecdPhotoCardIds((prevData) => {
      const exists = prevData.includes(photoCard.photoCardId);
      if (exists) {
        return prevData.filter((id) => id !== photoCard.photoCardId);
      }
      return [...prevData, photoCard.photoCardId];
    });
  };
  // #endregion

  // Function to handle the received paginated data
  const handlePaginatedData = (newPaginatedData) => {
    const updatedEvents = newPaginatedData.flatMap((page) =>
      page.events.map((event) => {
        const newEvent = {
          ...event,
          photoCards: event?.photoCards?.map((photoCard) => {
            const newIsChecked = checkecdPhotoCardIds.find(
              (photoCardId) => photoCardId === photoCard.photoCardId
            );
            return {
              ...photoCard,
              ...{ isChecked: newIsChecked }
            };
          })
        };
        return new EventResponse(newEvent);
      })
    );
    setPaginatedEvents(updatedEvents);
  };

  // Use useEffect to update paginatedData and updatedData on new data arrival
  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedData(data.pages);
      // setPhotoCardSummary(first(data.pages)?.meta?.photoCardsInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, checkecdPhotoCardIds]);

  // 자동 pagination 로직
  useEffect(() => {
    // 초기 로딩 중이거나 이미 자동 fetching 중이면 return
    if (isFetching || isAutoFetching || isUndefined(paginatedEvents)) {
      return;
    }

    // 현재 데이터에 유효한 photoCard가 있으면 자동 fetching 완료
    if (hasValidPhotoCards(paginatedEvents)) {
      setHasAttemptedFullLoad(true);
      return;
    }

    // 더 불러올 페이지가 있으면 자동으로 다음 페이지 로드
    if (hasNextPage && !hasAttemptedFullLoad) {
      setIsAutoFetching(true);
      fetchNextPage().finally(() => {
        setIsAutoFetching(false);
      });
    } else {
      // 더 이상 불러올 데이터가 없으면 전체 로드 완료로 표시
      setHasAttemptedFullLoad(true);
    }
  }, [paginatedEvents, hasNextPage, isFetching, isAutoFetching, hasAttemptedFullLoad, fetchNextPage]);

  // 필터나 아티스트 변경 시 자동 fetching 상태 초기화
  useEffect(() => {
    setHasAttemptedFullLoad(false);
    setIsAutoFetching(false);
  }, [currentArtist, currentMember, selectedEventIds, selectedPhotoCardCategories, isPendingMode]);

  // #region events
  const loadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const onChangedArtist = (artist) => {
    setCurrentArtist(artist);
  };

  // 멤버 선택 변경
  const onChangedMember = (member) => {
    setCurrentMember(member);
  };

  // 네비게이션 뒤로가기 눌렀을 경우
  const onHistoryBackClick = () => {
    if (isEmpty(checkecdPhotoCardIds)) {
      return IgnoreAndExit();
    }
    return setIsOpenExitDialog(true);
  };

  const onChangedBoxTab = (tabIndex) => {
    setIsPendingMode(tabIndex === 0);
    if (!isEmpty(checkecdPhotoCardIds) && boxTabIndex !== tabIndex) {
      setCheckecdPhotoCardIds([]);
    }
    setBoxTabIndex(tabIndex);
  };

  const IgnoreAndExit = () => {
    setCheckecdPhotoCardIds([]);
    setIsOpenExitDialog(false);
    return handleHistoryBack();
  };

  const onConfirmedPhotoCard = (photoCard) => {
    const isChecked = photoCard.isChecked ?? false;
    handleConfirmedPhotoCard({
      ...photoCard,
      isChecked: !isChecked
    });
  };

  const onDoneConfirm = async () => {
    if (isEmpty(checkecdPhotoCardIds)) {
      return alert('승인할 사진이 없습니다.');
    }

    ConfirmPhotoCard(checkecdPhotoCardIds).then((res) => {
      if (res.ok) {
        setCheckecdPhotoCardIds([]);
        alert(`${checkecdPhotoCardIds.length}장 승인 완료`);
        queryClient.clear();
      } else {
        alert('오류 발생. 관리자에 문의해주세요.');
      }
    });
  };

  const onDoneReject = async () => {
    if (isEmpty(checkecdPhotoCardIds)) {
      return alert('승인거절할 사진이 없습니다.');
    }

    RejectPhotoCard(checkecdPhotoCardIds).then((res) => {
      if (res.ok) {
        setCheckecdPhotoCardIds([]);
        alert(`${checkecdPhotoCardIds.length}장 승인거절 완료`);
        queryClient.clear();
      } else {
        alert('오류 발생. 관리자에 문의해주세요.');
      }
    });
  };

  const onChangePhotoCardFilter = () => {
    setIsShowFilterBottomDrawer(!isShowFilterBottomDrawer);
  };

  const onConfirmPhotoCardFilter = (filteredObj) => {
    setIsShowFilterBottomDrawer(false);

    if (!isEmpty(filteredObj)) {
      setSelectedEventIds(filteredObj.eventIds);
      setSelectedPhotoCardCategories(filteredObj.photoCardCategories);

      if (currentArtist?.artistId) {
        queryClient.invalidateQueries(['photoCardEvents', currentArtist?.artistId]);
      }
      queryClient.invalidateQueries(['collectedPhotoCardIds']);
    }
  };

  const isCheckedPhotoCardFilter = () => {
    return !isEmpty(selectedPhotoCardCategories) || !isEmpty(selectedEventIds);
  };
  // #endregion

  // 렌더링 조건을 결정하는 함수
  const getRenderContent = () => {
    // 초기 로딩 중이거나 paginatedEvents가 아직 설정되지 않은 경우
    if (isUndefined(paginatedEvents)) {
      return <EmptyPhotoCardEventSkeletonV2 variant="legacyReported" />;
    }

    // 자동 fetching 중이거나 일반 fetching 중인 경우 (하지만 데이터가 있으면 보여줌)
    if ((isAutoFetching || isFetching) && !hasValidPhotoCards(paginatedEvents)) {
      return <EmptyPhotoCardEventSkeletonV2 variant="legacyReported" />;
    }

    // 유효한 photoCard 데이터가 있는 경우
    if (hasValidPhotoCards(paginatedEvents)) {
      return (
        <div className="w-full">
          <PhotoCardsByEventEditor
            events={paginatedEvents}
            onClickedPhotoCard={(photoCard, _) => onConfirmedPhotoCard(photoCard)}
            endReached={loadMore}
            enableAll={false}
            isAdminMode={true}
          />
          {(isFetching || isAutoFetching) && (
            <Center marginTop={'8px'}>
              <Spinner
                size="sm"
                className="text-primary-light dark:text-primary-dark"
                marginRight={'20px'}
              />
            </Center>
          )}
        </div>
      );
    }

    // 모든 페이지를 확인했지만 유효한 데이터가 없는 경우에만 EmptyCase 표시
    if (hasAttemptedFullLoad && !hasNextPage && !isFetching && !isAutoFetching) {
      return <EmptyCase emoji={'😊'} title={t('photoCardPage.emptyPhotoCards')} />;
    }

    // 기본적으로 로딩 중 표시
    return <EmptyPhotoCardEventSkeletonV2 variant="legacyReported" />;
  };

  return (
    <AdminProtected>
      <PageWrapper className={`${colorMode === 'dark' ? 'darkHomeBackground' : 'homeBackground'}`}>
        <Helmet>
          <title>{t('tabs.collections')}</title>
          <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
        </Helmet>
        <Header
          onConfirm={onDoneConfirm}
          onReject={onDoneReject}
          onHistoryBackClick={onHistoryBackClick}
        />
        <div
          style={{
            paddingTop: 'calc(64px + env(safe-area-inset-top))',
            paddingLeft: '20px',
            paddingRight: '20px'
          }}
        >
          <BoxTabs
            tabLabels={[t('ReportedPhotoCardsPage.pending'), t('entire')]}
            onTabSelected={(tabIndex) => onChangedBoxTab(tabIndex)}
          />
        </div>
        <PhotoCardsSummaryRow
          artists={artists}
          onChangeArtist={onChangedArtist}
          isCheckedPhotoCardFilter={isCheckedPhotoCardFilter}
          onChangePhotoCardFilter={onChangePhotoCardFilter}
        />
        <LikedMembersListWrapper>
          <LikedMembersList
            artistMembers={currentArtist?.members}
            onChangeLikedMember={onChangedMember}
            showAllMember={true}
          />
          {getRenderContent()}
        </LikedMembersListWrapper>
        {isMyPhotoCardsBottomSheetOpen && (
          <MyPhotoCardBottomSheet
            isMyPhotoCardsBottomSheetOpen={isMyPhotoCardsBottomSheetOpen}
            setIsMyPhotoCardsBottomSheetOpen={setIsMyPhotoCardsBottomSheetOpen}
          />
        )}
        {isOpenExitDialog && (
          <CommonDialog
            isOpen={isOpenExitDialog}
            onClose={() => setIsOpenExitDialog(false)}
            description={t('photoCardPage.discardAlertDialog')}
            noButtonText={t('no')}
            yesButtonText={t('yes')}
            onClickDialogButton={() => IgnoreAndExit()}
          />
        )}
        {isShowFilterBottomDrawer && (
          <PhotoCardFilterBottomDrawer
            artistId={currentArtist?.artistId}
            isOpen={isShowFilterBottomDrawer}
            photoCardCategories={selectedPhotoCardCategories}
            eventIds={selectedEventIds}
            onClose={() => setIsShowFilterBottomDrawer(false)}
            onConfirm={(obj) => onConfirmPhotoCardFilter(obj)}
          />
        )}
      </PageWrapper>
    </AdminProtected>
  );
}

const Header = ({ onConfirm, onReject, onHistoryBackClick }) => {
  return (
    <Row
      className={'fixed top-0 safeAreaMarginTop px-[24px] py-[12px] w-full z-[999999] bg-pure-white dark:bg-pure-black'}
      style={{ maxWidth: 'var(--chakra-sizes-prose)' }}
    >
      <ArrowLeftIcon
        className="fill-pure-black dark:fill-pure-white"
        onClick={() => onHistoryBackClick()}
      />
      <Spacer />
      <div className="flex flex-row gap-1">
        <RegularSolidButton text={t('reject')} onClicked={onReject} />
        <RegularSolidButton text={t('approve')} onClicked={onConfirm} />
      </div>
    </Row>
  );
};

const DropdownTextOnly = ({ artist }) => {
  return (
    <Menu>
      <MenuButton
        height={'40px'}
        as={Button}
        colorScheme=""
        className="text-main-light-1 dark:text-main-dark-1"
        width={'fit-content'}
        padding={0}
        rightIcon={
          <Box height="40px" display="flex" alignItems="center" justifyContent="center">
            <ChevronDownIcon
              width={'22px'}
              height={'22px'}
              className="fill-main-light-3 dark:fill-main-dark-3"
              opacity={0}
            />
          </Box>
        }
      >
        <Box height="40px" display="flex" alignItems="center" justifyContent="center">
          <Text
            fontSize={'26px'}
            fontWeight={700}
            className="text-main-light-1 dark:text-main-dark-1"
          >
            {artist?.name}
          </Text>
        </Box>
      </MenuButton>
    </Menu>
  );
};

const PhotoCardsSummaryRow = ({
  artists,
  onChangeArtist,
  isCheckedPhotoCardFilter,
  onChangePhotoCardFilter
}) => {
  return (
    <Row className="items-center" style={{ padding: '12px 24px' }}>
      {artists ? (
        <ArtistTextSelector
          artists={artists}
          onSelected={onChangeArtist}
          alignDropdown="left"
          customTextAreaElementMultiple={(artist) => (
            <MenuButton
              height={'40px'}
              as={Button}
              colorScheme=""
              width={'fit-content'}
              padding={0}
              rightIcon={
                <Box
                  height="40px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  marginInlineStart={'-0.5rem'}
                  paddingLeft={'6px'}
                >
                  <ChevronDownIcon
                    width={'22px'}
                    height={'22px'}
                    className="fill-main-light-3 dark:fill-main-dark-3"
                  />
                </Box>
              }
            >
              <Box height="40px" display="flex" alignItems="center" justifyContent="center">
                <Text
                  fontSize={'26px'}
                  fontWeight={700}
                  className="text-main-light-1 dark:text-main-dark-1"
                >
                  {artist?.shortName}
                </Text>
              </Box>
            </MenuButton>
          )}
          customTextAreaElementSingle={(artist) => <DropdownTextOnly artist={artist} />}
        />
      ) : (
        <Text
          fontSize={'26px'}
          fontWeight={700}
          className="text-main-light-1 dark:text-main-dark-1"
        >
          {'-'}
        </Text>
      )}
      <Spacer />
      <SecondaryLineButton
        text={
          <FilterButtonText
            className={
              isCheckedPhotoCardFilter()
                ? 'text-primary-light dark:text-primary-dark'
                : 'text-main-light-1 dark:text-main-dark-1'
            }
            isChecked={isCheckedPhotoCardFilter()}
          >
            {t('photoCardPage.filter')}
          </FilterButtonText>
        }
        rightElement={
          <Switch
            size={'sm'}
            alignContent={'center'}
            colorScheme={'primary'}
            isChecked={isCheckedPhotoCardFilter()}
            onChange={() => onChangePhotoCardFilter()}
            style={{ '--switch-track-height': '0.6rem' }}
            // TODO Switch BGColor
          />
        }
        style={{
          margin: '1.5px 0px',
          boxShadow: isCheckedPhotoCardFilter()
            ? '0px 0px 5px 0px rgba(255, 118, 250, 0.2), 0px 0px 8px 0px rgba(161, 142, 255, 0.3)'
            : '',
          padding: '8px 14px',
          gap: '4px'
        }}
      />
    </Row>
  );
};
